import { useCallback, useEffect, useRef } from 'react';
import { updateChannelActive, updateClientChannelActive } from 'api';

export type ChannelActivityMessageKind = 'QUICK_REPLY_QUESTION';

type ActivityJob = {
  messageKind?: ChannelActivityMessageKind;
  resolve: (ok: boolean) => void;
  reject: (error: unknown) => void;
};

type UseChannelActivityParams = {
  channelId: string;
  isAuthenticated: boolean;
  clientId?: string;
  debounceMs: number;
};

const noopJob = (): ActivityJob => ({
  resolve: () => undefined,
  reject: () => undefined,
});

/**
 * Serializes `/sent` for one Chat channel.
 * Consecutive normals stay debounced; a question flushes earlier jobs first
 * and waits for its own update. An in-flight `/sent` is part of the queue.
 */
export function useChannelActivity({
  channelId,
  isAuthenticated,
  clientId,
  debounceMs,
}: UseChannelActivityParams) {
  const queueRef = useRef<ActivityJob[]>([]);
  const drainingRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const pendingNormalRef = useRef(false);
  const channelIdRef = useRef(channelId);
  channelIdRef.current = channelId;

  const sendSent = useCallback(
    async (messageKind?: ChannelActivityMessageKind): Promise<boolean> => {
      const options = messageKind ? { messageKind } : undefined;
      if (isAuthenticated) {
        return updateChannelActive(channelIdRef.current, options);
      }
      return updateClientChannelActive(
        channelIdRef.current,
        clientId || '',
        options,
      );
    },
    [clientId, isAuthenticated],
  );

  const drain = useCallback(async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    try {
      while (queueRef.current.length > 0) {
        const job = queueRef.current.shift();
        if (!job) break;
        try {
          const ok = await sendSent(job.messageKind);
          job.resolve(ok === true);
        } catch (error) {
          job.reject(error);
        }
      }
    } finally {
      drainingRef.current = false;
      if (queueRef.current.length > 0) {
        void drain();
      }
    }
  }, [sendSent]);

  const enqueue = useCallback(
    (messageKind?: ChannelActivityMessageKind): Promise<boolean> => {
      return new Promise<boolean>((resolve, reject) => {
        queueRef.current.push({ messageKind, resolve, reject });
        void drain();
      });
    },
    [drain],
  );

  const flushPendingNormal = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }
    if (pendingNormalRef.current) {
      pendingNormalRef.current = false;
      queueRef.current.push(noopJob());
    }
  }, []);

  const scheduleNormal = useCallback(() => {
    if (pendingNormalRef.current || debounceTimerRef.current) {
      return;
    }

    pendingNormalRef.current = true;
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = undefined;
      if (!pendingNormalRef.current) return;
      pendingNormalRef.current = false;
      queueRef.current.push(noopJob());
      void drain();
    }, debounceMs);
  }, [debounceMs, drain]);

  const flushQuestion = useCallback(async (): Promise<boolean> => {
    flushPendingNormal();
    return enqueue('QUICK_REPLY_QUESTION');
  }, [enqueue, flushPendingNormal]);

  useEffect(() => {
    const queue = queueRef;
    const pendingNormal = pendingNormalRef;
    const debounceTimer = debounceTimerRef;
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = undefined;
      }
      if (pendingNormal.current) {
        pendingNormal.current = false;
        queue.current.push(noopJob());
        void drain();
      }
    };
  }, [drain]);

  return { scheduleNormal, flushQuestion };
}
